import { cloneToMutableData } from '@app/shared/helpers';
import {
  CalcMethod,
  CollaringMode,
  DividendSetId,
  EventKey,
  OpmInputDataSource,
  StaticCaseSelectionOptions,
} from '@app/shared/models/contracts/enums/shared-enums';
import {
  CaseDto,
  EquityRefinancingEventSetDto,
  PerYearValuesDto,
  ProjectDto,
} from '@app/shared/models/contracts/project-dto';
import { OpmInputFormModel2 } from '@app/modules/projects/inputs/opm-inputs-2/OpmInputs';
import { AppDispatch } from '@app/core/store/store';
import { setCapitalStructureDividendsLastPwermCase } from '@app/core/store/ui-values-slice';

export const areCaseEventsSame = (
  cases: CaseDto[],
  eventSets: Record<string, EquityRefinancingEventSetDto>
): boolean => {
  if (cases.length <= 1) {
    return true;
  }

  const eventsPerCase = cases.map(
    (c) => new Set(c.eventSetId ? eventSets[c.eventSetId]?.events ?? [] : [])
  );

  const firstCaseEvents = eventsPerCase[0];
  return eventsPerCase.every(
    (caseEvents) =>
      caseEvents.size === firstCaseEvents.size &&
      [...firstCaseEvents].every((id) => caseEvents.has(id))
  );
};

type UpdatedOpmInput = OpmInputFormModel2 &
  Omit<import('@app/shared/models/contracts/project-dto').OpmInputDto, keyof OpmInputFormModel2>;

interface UpdateCaseSelectionParams {
  opmInput: UpdatedOpmInput;
  pwermCases: CaseDto[];
  isOpmOnlyProject: boolean;
  dispatch: AppDispatch;
}

export const updateCaseSelection = ({
  opmInput,
  pwermCases,
  isOpmOnlyProject,
  dispatch,
}: UpdateCaseSelectionParams): void => {
  const caseWeightedSetId = DividendSetId.CASE_WEIGHTED_DIVIDEND_SET_ID;
  const caseWeightedCaseId = StaticCaseSelectionOptions.CaseWeighted;
  const opmOnlySetId = DividendSetId.OPM_ONLY_DIVIDEND_SET_ID;
  const opmOnlyCaseId = StaticCaseSelectionOptions.OpmOnly;

  if (opmInput.selectedCaseId === caseWeightedCaseId) {
    opmInput.selectedDividendSetId = caseWeightedSetId;
  } else if (opmInput.selectedCaseId === opmOnlyCaseId) {
    opmInput.selectedDividendSetId = opmOnlySetId;
  } else {
    const pwermCase = pwermCases.find((caseItem) => caseItem.caseId === opmInput.selectedCaseId);
    if (pwermCase) {
      const eventSetIdForNoSelectedCase = isOpmOnlyProject
        ? EventKey.OpmOnly
        : EventKey.EmptyEventSet;
      opmInput.selectedEventSetId = pwermCase.eventSetId ?? eventSetIdForNoSelectedCase;
      opmInput.selectedDividendSetId = pwermCase.dividendSetId ?? null;
      if (pwermCase.dividendSetId) {
        dispatch(setCapitalStructureDividendsLastPwermCase(pwermCase.dividendSetId));
      }
    }
  }
};

interface HandleCaseSelectionChangeParams {
  newCaseId: string;
  project: ProjectDto;
  currentFormValues: OpmInputFormModel2;
  getAllOpmValuesFromPwerm: (forecastDates: string[]) => any[];
  dispatch: AppDispatch;
}

export const handleCaseSelectionChange = async ({
  newCaseId,
  project,
  currentFormValues,
  getAllOpmValuesFromPwerm,
  dispatch,
}: HandleCaseSelectionChangeParams): Promise<UpdatedOpmInput> => {
  const updatedOpmInput = cloneToMutableData({
    ...project.opmInput,
    ...currentFormValues,
    selectedCaseId: newCaseId,
  }) as UpdatedOpmInput;

  const isOpmOnlyProject = project.details.calcMethod === CalcMethod.OPM;

  updateCaseSelection({
    opmInput: updatedOpmInput,
    pwermCases: project.pwermInput.cases,
    isOpmOnlyProject,
    dispatch,
  });

  const pwermCase = project.pwermInput.cases.find((caseItem) => caseItem.caseId === newCaseId);

  if (pwermCase) {
    const allForecastDates = pwermCase.forecasts.map((forecast) => forecast.forecastYear);
    const pwermValues = getAllOpmValuesFromPwerm(allForecastDates);
    const opmForecastYears = updatedOpmInput.forecastYears;
    const pwermForecastYears = pwermCase.forecasts.length;
    for (let i = 0; i < opmForecastYears; i++) {
      const existingYear = updatedOpmInput.perYearInputs[i];
      if (i < pwermForecastYears) {
        const baseYearInput = pwermValues[i];
        const newForecastDate = allForecastDates[i];
        const newYearInput: PerYearValuesDto = {
          exitHorizonProbability: existingYear?.exitHorizonProbability ?? 0,
          riskFreeRate: existingYear?.riskFreeRate ?? 0,
          forecastDate: newForecastDate,
          equitySoldInIpo: baseYearInput?.equitySoldInIpo ?? 0,
          equitySoldInIpoSource: OpmInputDataSource.FromPWERM,
          financingCosts:
            existingYear?.financingCostsSource === OpmInputDataSource.Override
              ? existingYear?.financingCosts ?? 0
              : baseYearInput?.financingCosts ?? 0,
          financingCostsSource:
            existingYear?.financingCostsSource === OpmInputDataSource.Override
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM,
          hypotheticalDebtWhenIpo: baseYearInput?.hypotheticalDebtWhenIpo ?? 0,
          hypotheticalDebtWhenIpoSource: OpmInputDataSource.FromPWERM,
          ipoCostsOfEv: baseYearInput?.ipoCostsOfEv ?? 0,
          ipoCostsOfEvSource: OpmInputDataSource.FromPWERM,
          ipoDiscount: baseYearInput?.ipoDiscount ?? 0,
          ipoDiscountSource: OpmInputDataSource.FromPWERM,
          ipoProbability: baseYearInput?.ipoProbability ?? 0,
          ipoProbabilitySource: OpmInputDataSource.FromPWERM,
          postIpoSaleDlom: baseYearInput?.postIpoSaleDlom ?? 0,
          postIpoSaleDlomSource: OpmInputDataSource.FromPWERM,
          secondarySaleCostsOfEv: baseYearInput?.secondarySaleCostsOfEv ?? 0,
          secondarySaleCostsOfEvSource: OpmInputDataSource.FromPWERM,
          operationalFreeCashFlow: baseYearInput?.operationalFreeCashFlow ?? 0,
          operationalFreeCashFlowSource: OpmInputDataSource.FromPWERM,
          operationalEv: baseYearInput?.operationalEv ?? 0,
          operationalEvSource: OpmInputDataSource.FromPWERM,
          netDebtValues: baseYearInput?.netDebtItems ?? [],
          cashValue: baseYearInput?.cashValue ?? 0,
          totalNetDebt: baseYearInput?.totalNetDebt ?? 0,
          totalNetDebtSource: OpmInputDataSource.FromPWERM,
          marketValueDloms: baseYearInput?.marketValueDloms ?? [],
          collaring: { inputMode: CollaringMode.Off, percentage: null, fixedValue: null },
        };
        updatedOpmInput.perYearInputs[i] = newYearInput;
      }
    }
  }

  return updatedOpmInput;
};
